import React, { useState, useEffect } from "react";
import {
  Flex,
  Box,
  Text,
  Button,
  Table,
  Divider,
} from "@fluentui/react-northstar";
import BarChart from "../Charts/BarChart";
import { useTranslation } from "react-i18next";

//  Extension Tab Component
//  Displays credit usage by extension and features

const ExtensionTab = ({ data, creditUsage, isLoading }) => {
  const { t } = useTranslation();

  const translation = t("aiConsumption").extension;
  const commonTranslation = t("aiConsumption").common;

  // State for view filter (teachers/students)
  const [viewBy, setViewBy] = useState("teachers");

  // State for selected extension (for feature breakdown)
  const [selectedExtension, setSelectedExtension] = useState(null);

  // Initialize selected extension when data changes or viewBy changes
  useEffect(() => {
    if (creditUsage && creditUsage[viewBy]) {
      const extensions = Object.keys(creditUsage[viewBy]);
      if (extensions.length > 0) {
        setSelectedExtension(extensions[0]);
      }
    }
  }, [creditUsage, viewBy]);

  if (isLoading) return <Text content="Loading data..." />;

  // Prepare data for the main extension bar chart
  const extensions = Object.keys(creditUsage[viewBy] || {});
  const extensionData = extensions.map((ext) => creditUsage[viewBy][ext].total);

  // Colors for the charts
  const chartColors = [
    "#3B82F6", // Blue
    "#10B981", // Green
    "#F59E0B", // Amber
    "#8B5CF6", // Purple
    "#F43F5E", // Pink
  ];

  // Prepare data for the feature breakdown chart
  let featuresData = [];
  let featuresLabels = [];

  if (selectedExtension && creditUsage[viewBy][selectedExtension]) {
    const features = Object.entries(
      creditUsage[viewBy][selectedExtension]
    ).filter(([key]) => key !== "total");

    featuresLabels = features.map(([key]) => key);
    featuresData = features.map(([_, value]) => value);
  }

  return (
    <Box>
      <Flex space="between" vAlign="center" style={{ marginBottom: "1.5rem" }}>
        <Text
          size="large"
          weight="bold"
          content={translation.title}
          style={{ color: "#1F2937" }}
        />

        <Flex gap="gap.small">
          <Button
            content={commonTranslation.teachers}
            primary={viewBy === "teachers"}
            onClick={() => setViewBy("teachers")}
            style={{
              minWidth: "100px",
              background: viewBy === "teachers" ? "#3B82F6" : "#E5E7EB",
              color: viewBy === "teachers" ? "white" : "#4B5563",
            }}
          />
          <Button
            content={commonTranslation.students}
            primary={viewBy === "students"}
            onClick={() => setViewBy("students")}
            style={{
              minWidth: "100px",
              background: viewBy === "students" ? "#3B82F6" : "#E5E7EB",
              color: viewBy === "students" ? "white" : "#4B5563",
            }}
          />
        </Flex>
      </Flex>

      {/* Main Extension Bar Chart */}
      <BarChart
        title={translation.usageByExtension}
        categories={extensions}
        data={extensionData}
        colors={chartColors}
        yaxisTitle={commonTranslation.creditsUsed}
        height={350}
      />

      {/* Feature Breakdown Chart for selected extension */}
      {selectedExtension && (
        <BarChart
          title={`${translation.featureUsage} ${selectedExtension}`}
          categories={featuresLabels}
          data={featuresData}
          colors={
            chartColors[
              extensions.indexOf(selectedExtension) % chartColors.length
            ]
          }
          yaxisTitle={commonTranslation.creditsUsed}
          height={350}
        />
      )}

      <Divider style={{ margin: "2rem 0" }} />

      {/* Detailed Breakdown Table */}
      <Box style={{ overflowX: "auto" }}>
        <Text
          size="large"
          weight="semibold"
          content={translation.detailedBreakdown}
          style={{
            marginBottom: "1rem",
            color: "#374151",
          }}
        />

        <Table
          aria-label={translation.detailedCreditUsage}
          header={{
            items: [
              { content: translation.extension, key: "extension" },
              { content: translation.feature, key: "feature" },
              { content: translation.creditsUsed, key: "credits" },
              { content: translation.percentage, key: "percentage" },
            ],
          }}
          rows={Object.entries(creditUsage[viewBy]).flatMap(
            ([extension, features]) => {
              const extensionTotal = features.total;

              return Object.entries(features)
                .filter(([feature]) => feature !== "total")
                .map(([feature, credits]) => {
                  const percentage =
                    extensionTotal > 0
                      ? ((credits / extensionTotal) * 100).toFixed(1)
                      : "0.0";

                  return {
                    key: `${extension}-${feature}`,
                    items: [
                      { content: extension, key: "extension" },
                      { content: feature, key: "feature" },
                      { content: credits.toLocaleString(), key: "credits" },
                      { content: `${percentage}%`, key: "percentage" },
                    ],
                  };
                });
            }
          )}
        />
      </Box>
    </Box>
  );
};

export default ExtensionTab;
